// Contractual hours and gross pay arithmetic (C3 Workforce/RH — brief G2).
//
// Pure functions over FACTS ONLY. Read the scope note at the bottom before
// adding anything to this file: the settlement POLICY for overtime is
// deliberately absent, and that is a decision, not an omission.
//
// The unit of pay is the HOUR. An employee at €14/h who works 70 h in a month
// is owed €980 — not a prorated monthly salary. `brut_mensuel` on the contract
// is a reference figure for a full month at the contractual volume, never what
// is actually paid.

/** The French standard working week. */
export const STANDARD_WEEKLY_HOURS = 35;

/**
 * Weeks per month, exactly 52 ÷ 12. This is the legal conversion, not an
 * approximation of « about 4.33 » — it is what turns 35 h/week into the
 * familiar 151,67 h/month.
 */
export const WEEKS_PER_MONTH = 52 / 12;

/**
 * Contractual monthly hours for a weekly volume (35 → 151.67).
 *
 * DERIVED, never stored. Persisting both figures would create two homes for one
 * fact and, sooner or later, two answers — the same reasoning that keeps
 * `covers` computed from `halfBoards + diners`.
 */
export function monthlyHoursFor(weeklyHours: number): number {
  return Math.round(weeklyHours * WEEKS_PER_MONTH * 100) / 100;
}

/**
 * Gross pay in CENTS for hours actually worked.
 *
 * Cents throughout: €14/h × 70.25 h in floating point is 983.4999999999999, and
 * payroll is not a place for a value that is almost right. Callers hold integer
 * cents and format at the edge.
 */
export function grossPayCents(hoursWorked: number, hourlyRateCents: number): number {
  return Math.round(hoursWorked * hourlyRateCents);
}

/** €14/h → 1400 cents. Accepts the `brut_horaire` numeric as it comes off the row. */
export function hourlyRateToCents(bruteHoraire: number): number {
  return Math.round(bruteHoraire * 100);
}

/**
 * Hours worked ÷ contractual hours for the period. 1 = exactly at contract.
 *
 * Null when the contract carries no weekly volume: the allocator must then have
 * NO hours signal for that person, which is not the same as a signal of zero.
 * A missing contractual volume should never read as « this person is far below
 * their hours, give them everything ».
 */
export function hoursRatio(hoursWorked: number, contractualHours: number | null): number | null {
  if (contractualHours === null || contractualHours <= 0) return null;
  return hoursWorked / contractualHours;
}

// ── Monthly balance and carry-over ──────────────────────────────────────────

export interface MonthWorked {
  /** 'YYYY-MM'. */
  month: string;
  hoursWorked: number;
}

export interface MonthBalance {
  month: string;
  hoursWorked: number;
  contractualHours: number;
  /** Positive = worked beyond contract that month. */
  balance: number;
  /** Running total carried in from earlier months, before this month's balance. */
  carriedIn: number;
  /** carriedIn + balance — what rolls into the next month. */
  carriedOut: number;
}

/**
 * Roll a run of months into a running balance.
 *
 * This is the operator's rule as stated — « aim not to go into overtime, though
 * averaging over a few months is accepted, the excess rolling into the
 * following month » — expressed as ARITHMETIC and nothing more. It answers
 * « how many hours above or below contract is this person, cumulatively ».
 *
 * It deliberately does NOT answer « what must be paid for them, at what
 * majoration, and when does the count reset ». See the scope note below.
 */
export function rollingBalance(
  months: MonthWorked[],
  weeklyHours: number,
  openingCarry = 0,
): MonthBalance[] {
  const contractualHours = monthlyHoursFor(weeklyHours);
  let carry = openingCarry;

  return months.map((m) => {
    const balance = Math.round((m.hoursWorked - contractualHours) * 100) / 100;
    const carriedIn = carry;
    carry = Math.round((carriedIn + balance) * 100) / 100;
    return {
      month: m.month,
      hoursWorked: m.hoursWorked,
      contractualHours,
      balance,
      carriedIn,
      carriedOut: carry,
    };
  });
}

/**
 * How far into overtime the person is right now, in hours — the number the
 * allocator's `hoursOverTarget` penalty rides on. Never negative: being UNDER
 * contract is not a reason to penalise anyone, and the allocator has other
 * signals for « give this person more work ».
 */
export function overtimeHours(balances: MonthBalance[]): number {
  const last = balances[balances.length - 1];
  return last ? Math.max(0, last.carriedOut) : 0;
}

// ── Scope note — what this file must NOT grow into ──────────────────────────
//
// There is no majoration rate (25 % / 50 %), no contingent annuel, no
// annualisation scheme, and no rule for when a carried balance must be settled
// in pay or in rest. That is not an oversight.
//
// The operator's own framing (2026-07-29): « the rule to aim for is not to go
// overtime, although it is acceptable to average out over a few months, the
// overtime worked being rolled over the following month. This is accepted by
// the labour organisation, although I recognise that each accountant will
// handle it differently — MORE RESEARCH NEEDED HERE. »
//
// Encoding a settlement rule on that basis would be a guess with payroll and
// URSSAF consequences, which Hard Rule 1 forbids and which no amount of unit
// testing would make safe. What is safe, and sufficient for the staffing model,
// is the balance: it lets the allocator avoid piling work onto someone already
// over their hours without anyone having to decide what that overtime costs.
//
// When the accountant's position is confirmed, the settlement rule belongs in
// its own module with its own ADR — not appended here.
